// src/context/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { setSentryUser, addBreadcrumb } from '../services/sentry';

interface AuthContextType {
  isLoggedIn: boolean;
  login: (tokens?: { idToken: string; refreshToken: string }, userName?: string) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  login: () => {},
  logout: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    // Initialize from localStorage
    return !!localStorage.getItem('idToken');
  });

  // Update Sentry user context when login state changes
  useEffect(() => {
    if (isLoggedIn) {
      const userName = localStorage.getItem('userName');
      setSentryUser(userName || 'unknown', userName || undefined);
      addBreadcrumb('User logged in', 'auth', 'info', { userName });
    } else {
      setSentryUser(null);
      addBreadcrumb('User logged out', 'auth', 'info');
    }
  }, [isLoggedIn]);

  const login = (tokens?: { idToken: string; refreshToken: string }, userName?: string) => {
    // If tokens come from an API response, store them
    if (tokens) {
      localStorage.setItem('idToken', tokens.idToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    if(userName){
      localStorage.setItem('userName', userName);
    }
    setIsLoggedIn(true);
  };

  const logout = () => {
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userName');
    setIsLoggedIn(false);

    // Redirect to the login page (full page reload)
    window.location.href = '/'; 
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Convenience hook
export const useAuth = () => useContext(AuthContext);